import { inject, Injectable } from '@angular/core';
import { LoggerService } from '@lx/shared/logger';
import { OnlineQuoteToolStore } from '../store';
import { OnlineSchoolResolverService } from './online-school-resolver.service';
import { PriceCalculationService } from './price-calculation.service';

/**
 * Service responsible for bootstrapping UI behaviors after application initialization.
 *
 * This service is called from AppComponent.ngOnInit() to set up reactive behaviors
 * that respond to user interactions. It keeps the AppComponent clean and focused
 * on root-level UI concerns.
 *
 * Separation of concerns:
 * - APP_INITIALIZER: Essential data initialization (country, currency, store)
 * - AppBootstrapService: UI behavior setup (subscriptions, listeners)
 * - AppComponent: Navigation and root-level UI
 */
@Injectable({
  providedIn: 'root',
})
export class AppBootstrapService {
  private readonly onlineSchoolResolverService = inject(OnlineSchoolResolverService);
  private readonly priceCalculationService = inject(PriceCalculationService);
  private readonly store = inject(OnlineQuoteToolStore);
  private readonly logger = inject(LoggerService);

  /**
   * Bootstrap all UI behaviors that should run after app initialization.
   *
   * This is called from AppComponent.ngOnInit() after the store is initialized
   * and the router is ready.
   */
  bootstrapUiBehaviors(): void {
    this.setupAutomaticPriceCalculation();
  }

  /**
   * Sets up automatic price calculation based on course selection changes.
   *
   * This:
   * 1. Fetches the online school code for the user's detected country
   * 2. Saves the school code to the store (automatically persisted to sessionStorage)
   * 3. Initializes the price calculation service to listen for selection changes
   * 4. Automatically calculates prices when user selects course/duration/dates
   *
   * @private
   */
  private setupAutomaticPriceCalculation(): void {
    this.onlineSchoolResolverService.getOnlineSchoolCode().subscribe({
      next: (onlineSchoolCode) => {
        this.store.updateSelectedSchool(onlineSchoolCode);
        this.priceCalculationService.initializePriceCalculation(onlineSchoolCode);
      },
      error: (error) => {
        this.logger.error('Failed to fetch online school code for price calculation', error);
      },
    });
  }
}
